/**
 * 🧱 Refactor BT Phase 3 — Étape 2 : section « SectionProduits » d'EditerBonTravailDialog.jsx.
 * JSX déplacé TEL QUEL (ex-L2580-2682, dédenté de 10) ; variables lues via `useBTForm()`
 * (BTFormProvider enrichi par `extra` dans le composant parent). DOM, data-testid, classes, textes inchangés.
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Plus, Trash2 } from 'lucide-react';
import { ProductSearchCombobox } from '../../ProductSearchCombobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBTForm } from '../../BTFormContext';

export function SectionProduits() {
  const {
    addProduit,
    bon,
    emplacementLabel,
    form,
    isExt,
    produits,
    removeProduit,
    setForm,
    type,
    updateProduit,
  } = useBTForm();
  return (
    <>
      {/* Produits */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-base">Produits</CardTitle>
            <Button type="button" size="sm" variant="outline" onClick={addProduit} disabled={form.produits_fournis_par_client}><Plus className="w-3 h-3 mr-1" /> Ajouter</Button>
          </div>
          {/* 🆕 [Phase 30 #2] Toggle "Produits fournis par le client" */}
          <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer select-none" data-testid="toggle-produits-fournis-client">
            <input
              type="checkbox"
              checked={!!form.produits_fournis_par_client}
              onChange={(e) => setForm({ ...form, produits_fournis_par_client: e.target.checked })}
              className="w-4 h-4 accent-indigo-600"
              data-testid="checkbox-produits-fournis-client"
            />
            <span className="text-slate-700">📦 <strong>Produits fournis par le client</strong> — pas de produits à commander, le client apporte tout</span>
          </label>
        </CardHeader>
        <CardContent>
          {form.produits_fournis_par_client ? (
            <p className="text-sm text-indigo-700 italic bg-indigo-50 border border-indigo-200 rounded p-3" data-testid="msg-produits-fournis-client">
              Le client fournit les produits/matériaux. Vous pouvez envoyer la présentation sans liste de produits.
            </p>
          ) : form.produits_selectionnes.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun produit ajouté</p>
          ) : (
            <div className="space-y-2">
              {form.produits_selectionnes.map((p, idx) => {
                const produit = produits.find(pr => pr.id === p.produit_id);
                const finis = produit?.finis_disponibles || produit?.finis || [];
                const isLocked = p.verrouille || (bon.premiere_commande_verrouillee && p.commande_initiale_faite);
                return (
                  <div key={idx} className={`border rounded-lg p-3 space-y-2 ${isLocked ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50'}`}>
                    {isLocked && <p className="text-xs text-emerald-600 font-medium flex items-center gap-1"><Lock className="w-3 h-3" /> Produit verrouillé (première commande)</p>}
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-4">
                        <Label className="text-xs">Produit</Label>
                        <ProductSearchCombobox
                          produits={produits}
                          value={p.produit_id}
                          onSelect={(prod) => updateProduit(idx, 'produit_id', prod.id)}
                          disabled={isLocked}
                          testId={`produit-search-${idx}`}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Fini</Label>
                        <Select value={p.fini || undefined} onValueChange={(v) => updateProduit(idx, 'fini', v)} disabled={isLocked}>
                          <SelectTrigger className="h-9"><SelectValue placeholder="Fini..." /></SelectTrigger>
                          <SelectContent>{finis.length > 0 ? finis.map(f => (<SelectItem key={f} value={f}>{f}</SelectItem>)) : <SelectItem value="-" disabled>Aucun fini</SelectItem>}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-1">
                        <Label className="text-xs">Unité</Label>
                        {/* 🐛 [Phase 94 — 2026-07-08] LECTURE SEULE :
                          * L'unité est verrouillée sur celle du produit. Le catalogue
                          * a un produit distinct par unité (Gallon/Pinte/Chaudière).
                          * Pour changer d'unité, sélectionner un autre produit.
                          */}
                        <div
                          className="h-9 flex items-center justify-center bg-gray-100 text-gray-700 text-sm rounded px-2 border border-gray-200"
                          data-testid={`produit-unite-locked-${idx}`}
                          title="Verrouillé — sélectionner un autre produit pour changer l'unité"
                        >
                          🔒 {p.unite === 'pinte' ? 'Pinte' : p.unite === 'chaudiere' ? 'Chaudière' : 'Gallon'}
                        </div>
                      </div>
                      <div className="col-span-1">
                        <Label className="text-xs text-blue-600">Gal. prévus</Label>
                        <Input className="h-9 border-blue-200 bg-blue-50/50" type="number" min="0" value={p.quantite_prevue || ''} onChange={(e) => updateProduit(idx, 'quantite_prevue', parseInt(e.target.value) || 0)} placeholder="0" data-testid={`produit-prevision-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">{emplacementLabel}</Label>
                        <Input className="h-9" value={p.emplacement} onChange={(e) => updateProduit(idx, 'emplacement', e.target.value)} disabled={isLocked} placeholder={isExt ? 'Façade nord' : 'Cuisine'} />
                      </div>
                      <div className="col-span-1">
                        <Label className="text-xs">Couleur</Label>
                        <Input className="h-9" value={p.code_couleur || ''} onChange={(e) => updateProduit(idx, 'code_couleur', e.target.value)} placeholder="OC-17" />
                      </div>
                      <div className="col-span-1 flex items-end justify-center gap-1">
                        {!isLocked && (
                          <>
                            {/* ✅ FIX: Badge informatif au lieu de checkbox manuelle */}
                            {p.commande_initiale_faite ? (
                              <Badge className="bg-green-100 text-green-700 text-[10px]" title="Commandé via historique">Cmd ✓</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] text-slate-400" title="Utilisez le bouton Commander pour créer un historique">Non cmd</Badge>
                            )}
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeProduit(idx)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                          </>
                        )}
                        {isLocked && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Cmd OK</Badge>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

    </>
  );
}
